import { Box, IconButton, Popover, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useRef, useState } from 'react';
import '../css/fieldHelpHint.css';

const HOVER_DELAY_MS = 2000;

const FieldHelpHint = ({ title, children, className }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const openedByClickRef = useRef(false);
    const hoverTimerRef = useRef(null);

    const open = Boolean(anchorEl);

    const showPopover = useCallback((target) => {
        setAnchorEl(target);
    }, []);

    const hidePopover = useCallback(() => {
        setAnchorEl(null);
        openedByClickRef.current = false;
    }, []);

    const clearHoverTimer = useCallback(() => {
        if (hoverTimerRef.current) {
            clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
    }, []);

    const handleClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        clearHoverTimer();
        openedByClickRef.current = true;
        showPopover(event.currentTarget);
    };

    const handleMouseEnter = (event) => {
        clearHoverTimer();
        hoverTimerRef.current = setTimeout(() => {
            if (!openedByClickRef.current) {
                showPopover(event.currentTarget);
            }
        }, HOVER_DELAY_MS);
    };

    const handleMouseLeave = () => {
        clearHoverTimer();
        if (!openedByClickRef.current) {
            hidePopover();
        }
    };

    const handleClose = () => {
        hidePopover();
    };

    return (
        <>
            <IconButton
                className={`fieldHelpHint_button ${className || ''}`}
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                aria-label={title || 'Aide'}
                size="small"
            >
                <Box component="span" className="fieldHelpHint_icon" aria-hidden="true">
                    !
                </Box>
            </IconButton>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { className: 'fieldHelpHint_popover' } }}
            >
                {title && (
                    <Typography className="fieldHelpHint_popover_title">{title}</Typography>
                )}
                <Typography className="fieldHelpHint_popover_body" component="div">
                    {children}
                </Typography>
            </Popover>
        </>
    );
};

FieldHelpHint.propTypes = {
    title: PropTypes.string,
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
};

export default FieldHelpHint;
